import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CreditCard,
  Wallet,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export const Route = createFileRoute('/_authenticated/cuentas')({
  component: CuentasPage,
});

interface BankAccount {
  id: string;
  alias: string;
  bankName: string;
  accountNumber: string;
  accountType: string;
  holderName: string;
  cbu: string;
  cuit?: string;
  currency: string;
  isActive: boolean;
  dailyLimit?: number;
  createdAt: string;
}

const ACCOUNT_TYPES: Record<string, string> = {
  transferencia: 'Transferencia',
  mp: 'MercadoPago',
  crypto_btc: 'Crypto BTC',
  crypto_usdt: 'Crypto USDT',
  other: 'Otro',
};

const formSchema = z.object({
  alias: z.string().min(1, 'Requerido'),
  bankName: z.string().min(1, 'Requerido'),
  accountNumber: z.string().min(1, 'Requerido'),
  accountType: z.enum(['transferencia', 'mp', 'crypto_btc', 'crypto_usdt', 'other']),
  holderName: z.string().min(1, 'Requerido'),
  cbu: z.string().min(1, 'Requerido'),
  cuit: z.string().optional(),
  currency: z.string().default('ARS'),
  dailyLimit: z.coerce.number().min(0).optional(),
});

type FormValues = z.infer<typeof formSchema>;

function CuentasPage() {
  const qc = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [deleting, setDeleting] = useState<BankAccount | null>(null);

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const { data } = await apiClient.get('/admin/bank-accounts');
      return (data ?? []) as BankAccount[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: FormValues) => {
      const { data } = await apiClient.post('/admin/bank-accounts', body);
      return data;
    },
    onSuccess: () => {
      toast.success('Cuenta creada');
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      setIsOpen(false);
      form.reset();
    },
    onError: (err: Error) => toast.error(err.message || 'Error'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: FormValues }) => {
      const { data } = await apiClient.patch(`/admin/bank-accounts/${id}`, body);
      return data;
    },
    onSuccess: () => {
      toast.success('Cuenta actualizada');
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      setIsOpen(false);
      setEditing(null);
      form.reset();
    },
    onError: (err: Error) => toast.error(err.message || 'Error'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/bank-accounts/${id}`);
    },
    onSuccess: () => {
      toast.success('Cuenta eliminada');
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      setDeleting(null);
    },
    onError: (err: Error) => toast.error(err.message || 'Error'),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      alias: '',
      bankName: '',
      accountNumber: '',
      accountType: 'transferencia',
      holderName: '',
      cbu: '',
      cuit: '',
      currency: 'ARS',
      dailyLimit: undefined,
    },
  });

  const onSubmit = (values: FormValues) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, body: values });
    } else {
      createMutation.mutate(values);
    }
  };

  const handleEdit = (acc: BankAccount) => {
    setEditing(acc);
    form.reset({
      alias: acc.alias,
      bankName: acc.bankName,
      accountNumber: acc.accountNumber,
      accountType: acc.accountType as any,
      holderName: acc.holderName,
      cbu: acc.cbu,
      cuit: acc.cuit || '',
      currency: acc.currency,
      dailyLimit: acc.dailyLimit,
    });
    setIsOpen(true);
  };

  const columns = useMemo<ColumnDef<BankAccount>[]>(
    () => [
      {
        accessorKey: 'alias',
        header: 'Alias',
        cell: ({ row }) => <div className="font-medium">{row.original.alias}</div>,
      },
      {
        accessorKey: 'bankName',
        header: 'Banco',
        cell: ({ row }) => <span>{row.original.bankName}</span>,
      },
      {
        accessorKey: 'accountType',
        header: 'Tipo',
        cell: ({ row }) => (
          <Badge variant="outline">{ACCOUNT_TYPES[row.original.accountType] || row.original.accountType}</Badge>
        ),
      },
      {
        accessorKey: 'holderName',
        header: 'Titular',
        cell: ({ row }) => <span>{row.original.holderName}</span>,
      },
      {
        accessorKey: 'cbu',
        header: 'CBU/CVU',
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.cbu}</span>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Activa' : 'Inactiva'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button size="icon" variant="ghost" onClick={() => handleEdit(row.original)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-red-500"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const activeCount = accounts.filter((a) => a.isActive).length;

  return (
    <>
      <TopBanner
        title="Cuentas"
        subtitle="Cuentas bancarias receptoras"
        action={
          <Button
            onClick={() => {
              setEditing(null);
              form.reset();
              setIsOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nueva cuenta
          </Button>
        }
      />
      <div className="flex-1 overflow-auto p-6">
        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-primary/10 p-2.5">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{accounts.length}</div>
                <div className="text-xs text-muted-foreground">Total cuentas</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-green-100 p-2.5">
                <CreditCard className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{activeCount}</div>
                <div className="text-xs text-muted-foreground">Activas</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-blue-100 p-2.5">
                <Wallet className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {accounts.filter((a) => a.accountType === 'transferencia').length}
                </div>
                <div className="text-xs text-muted-foreground">Transferencias</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DataTable
          columns={columns}
          data={accounts}
          isLoading={isLoading}
          searchPlaceholder="Buscar cuenta..."
          searchColumn="alias"
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar cuenta' : 'Nueva cuenta bancaria'}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="alias"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Alias</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="juan.galicia" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bankName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Banco</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Banco Galicia" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="transferencia">Transferencia</SelectItem>
                          <SelectItem value="mp">MercadoPago</SelectItem>
                          <SelectItem value="crypto_btc">Crypto BTC</SelectItem>
                          <SelectItem value="crypto_usdt">Crypto USDT</SelectItem>
                          <SelectItem value="other">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="accountNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número de cuenta</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="holderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titular</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cbu"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CBU/CVU</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="cuit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CUIT (opcional)</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dailyLimit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Límite diario ($)</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar cuenta?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  Se eliminará <strong>{deleting.alias}</strong> ({deleting.bankName}).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => deleting && deleteMutation.mutate(deleting.id)} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
